/**
 * New-user setup checklist.
 *
 * Rendered at the top of the dashboard until the user finishes (or dismisses)
 * the setup tasks. Item completion is computed server-side from existing data
 * (`compute_setup_checklist` in `data/db.py`); this component is pure render +
 * a single dismiss mutation against the existing `/api/patient/onboarding-complete`
 * endpoint.
 *
 * States (see `SetupChecklist.state` in openapi.yaml):
 *   - 'incomplete' → show progress bar + clickable items.
 *   - 'celebrate'  → one-shot "All set!" view; auto-fires the dismiss mutation
 *                    on mount so the card disappears on the next dashboard load.
 *   - 'dismissed'  → component renders null (server sets `visible: false`).
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useDashboard } from '@pelvia/shared/hooks/patient/useDashboard'
import { client, unwrap } from '@pelvia/shared/api'

// Once setup is finished/dismissed the card never returns. Remembering that
// locally lets us skip the reserved loading placeholder for established users
// (so it doesn't reserve-then-collapse on every cold load) while genuinely-new
// users still get a height-matched slot for a shift-free card swap.
const SETUP_DONE_KEY = 'pelvia_setup_done'

function useDismissChecklist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => unwrap(client.POST('/api/patient/onboarding-complete')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'dashboard'] })
    },
  })
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function SetupChecklistSkeleton() {
  return (
    <section
      className="setup-checklist-card setup-checklist-card-skeleton"
      aria-busy="true"
      aria-label="Loading setup checklist"
    >
      <div className="setup-checklist-header">
        <div className="setup-checklist-heading">
          <span className="skeleton-block" style={{ width: 160, height: 18 }} />
          <span className="skeleton-block" style={{ width: 110, height: 12, marginTop: 6 }} />
        </div>
      </div>
      <span className="skeleton-block" style={{ width: '100%', height: 8, borderRadius: 999 }} />
      <ul className="setup-checklist-items">
        {Array.from({ length: 4 }).map((_, i) => (
          <li key={i} className="setup-checklist-item">
            <span className="skeleton-block" style={{ width: '100%', height: 44, borderRadius: 10 }} />
          </li>
        ))}
      </ul>
    </section>
  )
}

export default function SetupChecklistCard() {
  const { data: dashboard, isPending } = useDashboard()
  const checklist = dashboard?.setup_checklist
  const dismiss = useDismissChecklist()

  const [setupDone, setSetupDone] = useState(() => {
    try { return localStorage.getItem(SETUP_DONE_KEY) === '1' } catch { return false }
  })

  // Celebrate state: fire the dismiss once on mount so this is the last time
  // the user sees the card. Re-runs are guarded by isPending/isSuccess on the
  // mutation itself and the ref below (mount can re-trigger if the parent
  // remounts before the dashboard refetches).
  const didCelebrateDismiss = useRef(false)
  useEffect(() => {
    if (
      checklist?.state === 'celebrate' &&
      !didCelebrateDismiss.current &&
      !dismiss.isPending &&
      !dismiss.isSuccess
    ) {
      didCelebrateDismiss.current = true
      dismiss.mutate()
    }
  }, [checklist?.state, dismiss])

  // Once the server confirms the card is gone, remember it so later cold loads
  // skip the reserved placeholder entirely.
  useEffect(() => {
    if (dashboard && (!checklist || !checklist.visible) && !setupDone) {
      try { localStorage.setItem(SETUP_DONE_KEY, '1') } catch { /* quota */ }
      setSetupDone(true)
    }
  }, [dashboard, checklist, setupDone])

  // Reserve the card's height while the dashboard loads (new users only), so the
  // real card resolves in place instead of shoving the feed down on arrival.
  // Card *visibility* stays purely server-driven below — the flag only gates this
  // placeholder, never the card itself.
  if (!dashboard && isPending && !setupDone) {
    return <SetupChecklistSkeleton />
  }

  if (!checklist || !checklist.visible) return null

  const { items, completed_count, total_count, percent, state } = checklist

  if (state === 'celebrate') {
    return (
      <section className="setup-checklist-card setup-checklist-card-celebrate" aria-label="Setup complete">
        <div className="setup-checklist-celebrate-icon" aria-hidden>
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div className="setup-checklist-celebrate-copy">
          <h2 className="setup-checklist-celebrate-title">You’re all set</h2>
          <p className="setup-checklist-celebrate-text">
            Nice work — your account is ready. Keep logging daily to unlock pattern and cycle insights.
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="setup-checklist-card" aria-label="Finish setting up your account">
      <header className="setup-checklist-header">
        <div className="setup-checklist-heading">
          <h2 className="setup-checklist-title">Finish setting up</h2>
          <span className="setup-checklist-counter">
            {completed_count} of {total_count} complete
          </span>
        </div>
        <button
          type="button"
          className="setup-checklist-dismiss"
          onClick={() => dismiss.mutate()}
          disabled={dismiss.isPending}
          aria-label="Dismiss setup checklist"
          title="Dismiss"
        >
          ×
        </button>
      </header>

      <div className="setup-checklist-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
        <div className="setup-checklist-progress-fill" style={{ width: `${percent}%` }} />
      </div>

      <ul className="setup-checklist-items">
        {items.map((item) => (
          <li
            key={item.id}
            className={`setup-checklist-item ${item.completed ? 'is-complete' : ''}`}
          >
            <Link to={item.link} className="setup-checklist-item-link">
              <span
                className="setup-checklist-check"
                aria-label={item.completed ? 'Completed' : 'Not yet complete'}
              >
                {item.completed && <CheckIcon />}
              </span>
              <span className="setup-checklist-item-copy">
                <span className="setup-checklist-item-label">{item.label}</span>
                <span className="setup-checklist-item-desc">{item.description}</span>
              </span>
              <span className="setup-checklist-item-chevron"><ChevronIcon /></span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
